import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './SsoCallback.module.css';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

/**
 * SSO Callback — Streaming Economics
 *
 * Flow:
 *   1. User arrives: /sso/callback?token=abc123
 *   2. Calls /streaming-economics/auth/sso-login to verify + get platform session
 *   3. Stores session token → redirects to home
 */
export function SsoCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMsg, setErrorMsg] = useState('');
  const verifiedRef = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token || verifiedRef.current) return;
    verifiedRef.current = true;

    const verifySSO = async () => {
      try {
        const resp = await fetch(`${API_BASE}/streaming-economics/auth/sso-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const data = await resp.json();

        if (!resp.ok) {
          setStatus('error');
          setErrorMsg(data.error || 'SSO verification failed');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Store session — same keys as the Login page uses
        localStorage.setItem('auth_token', data.token);
        if (data.user) {
          localStorage.setItem('user_email', data.user.email ?? '');
          localStorage.setItem('user_name', data.user.full_name ?? `${data.user.firstName ?? ''} ${data.user.lastName ?? ''}`.trim());
          localStorage.setItem('user_role', data.user.role ?? '');
          localStorage.setItem('account_id', String(data.user.accountId ?? data.user.account_id ?? ''));
        }

        setStatus('success');
        setTimeout(() => { window.location.href = '/'; }, 500);

      } catch {
        setStatus('error');
        setErrorMsg('Network error — could not verify SSO token');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    verifySSO();
  }, [searchParams, navigate]);

  return (
    <div className={styles.base}>
      <img
        src={'https://cdn-fastly.parrotanalytics.com/partners/parrot-logo-dark.png'}
        alt={'Parrot Analytics'}
        className={styles.logo}
      />
      <div className={styles.content}>
        {status === 'verifying' && (
          <>
            <svg className={styles.spinner} width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
            <h2 className={styles.title}>{'Signing you in...'}</h2>
            <p className={styles.subtitle}>{'Verifying your identity via Parrot SSO'}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <svg className={styles.check} width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
            <h2 className={styles.title}>{'Welcome to Streaming Economics'}</h2>
            <p className={styles.subtitle}>{'Redirecting...'}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <svg className={styles.error} width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            <h2 className={styles.title}>{'Sign-in Failed'}</h2>
            <p className={styles.subtitle}>{errorMsg}</p>
            <p className={styles.redirect}>{'Redirecting to login...'}</p>
          </>
        )}
      </div>
    </div>
  );
}
